import { type JSX,useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { Stack } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { oemTenantId } from 'app/config';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectCurrentOperation,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { EntitiesMultipleSelect } from 'common/components/EntitiesMultipleSelect';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import {
    selectPageInfo,
    selectPaginationGridData,
    setPaginationGridData
} from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ExpandableTextArea, ExpandableTextAreaProps } from 'common/components/sharedCustomComponents/ExpandableTextArea/ExpandableTextArea';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { UserProfilesDropDown } from 'common/components/sharedCustomComponents/userProfiles/UserProfilesDropDown';
import { WarningMessageWithCheckboxAcknowledgement } from 'common/components/sharedCustomComponents/WarningMessageWithCheckboxAcknowledgement/WarningMessageWithCheckboxAcknowledgement';
import { INACTIVE_IDENTITY_CHECKBOX_LABEL, INACTIVE_IDENTITY_WARNING_MESSAGE } from 'common/constants/inactiveIdentityCheckDisplayMessages';
import { Roles } from 'common/constants/roles';
import { useAuthContext } from 'common/contexts/AuthContext';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import {
    fetchTenantUserPaginatedMemberships,
    postTenantUserMembership,
    putTenantUserMembership
} from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { GlobalRole, UserProfileOemMapping,UserTenantMembership } from 'common/types/Entities';
import { EditDrawerContentProps, IEditingItemInfo, Operations } from 'common/types/TypeDefs';
import { dateToFrontEndFormat, isStartDateBeforeEndDate, toESTDateString } from 'common/utils/dateHelpers';
import { disableIfMissingAnyRole as disableFieldEvaluator } from 'common/utils/DisableButtonEvaluator';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { selectGlobalRolesByTenantId } from '../globalRoles/globalRolesSlice';

import { selectFilters } from './tenantUserMembershipSlice';

const renderGlobalRoleName = (entity: GlobalRole) => <>{entity.name}</>;

interface IDatesValidation {
    hasError: boolean;
    errorMessage: string;
}

interface UserTenantItem {
    rawGlobalRoles: GlobalRole[];
}

interface UserTenantDrawerProperties {
    tenantName: string;
    tenantId: string;
}

interface UseTenantUserMembershipDrawerProps {
    entityName: string;
    drawerProps: UserTenantDrawerProperties;
    editingItemInfo?: IEditingItemInfo<UserTenantMembership & UserTenantItem>;
}

function useTenantUserMembershipDrawer({
    entityName,
    drawerProps,
    editingItemInfo
}: UseTenantUserMembershipDrawerProps) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { currentUserRoles } = useAuthContext();

    // Selectors
    const paginationInfo = useAppSelector(selectPaginationGridData) ?? { currentPage: 0, rowsPerPage: 0 };
    const filterProp = useAppSelector(selectFilters);
    const action = useAppSelector(selectCurrentOperation) as Operations.Create | Operations.Edit | Operations.Link;
    const nameInConflict = useAppSelector(selectNameInConflict) ?? false;
    const isOpenDrawer = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);

    // State
    const [processing, setProcessing] = useState<boolean>(false);
    const [userProfile, setUserProfile] = useState<UserProfileOemMapping | null>(null);
    const [currentGlobalRoles, setCurrentGlobalRoles] = useState<string[]>([]);
    const [notes, setNotes] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [disableDependantFields, setDisableDependantFields] = useState<boolean>(false);
    const [isOEMTenant, setIsOEMTenant] = useState<boolean>(false);
    const [datesValidation, setDatesValidation] = useState<IDatesValidation>({
        hasError: false,
        errorMessage: 'The end date must be greater than the start date.'
    });
    const [inactiveIdentity, setInactiveIdentity] = useState<boolean>(false);
    const [inactiveIdentityChecked, setInactiveIdentityChecked] = useState<boolean>(false);

    // Derived values
    const currentPage = paginationInfo.currentPage ?? 0;
    const rowsPerPage = paginationInfo.rowsPerPage ?? 10;
    const sortModel = paginationInfo.sortModel;
    const globalRoles = useAppSelector(selectGlobalRolesByTenantId(isOEMTenant));
    const allowedRolesToModifyGlobalRolesField = [Roles.GlobalRoleManager, Roles.Admin];
    const shouldDisableGlobalRoleField = disableFieldEvaluator(currentUserRoles, allowedRolesToModifyGlobalRolesField);

    // Helper functions
    const userProfileInfoExists = useCallback((userProfileParam: UserProfileOemMapping | null): boolean => {
        const isUserProfileInfo = userProfileParam?.oemId ??
            userProfileParam?.firstName ??
            userProfileParam?.lastName;
        return !!isUserProfileInfo;
    }, []);

    const isEditFormInfoExists = useCallback((
        operation?: Operations,
        editingItemInfoParam?: IEditingItemInfo<UserTenantMembership> | undefined
    ): boolean => {
        return operation === Operations.Edit && !!editingItemInfoParam?.editingRow;
    }, []);

    const formatDate = useCallback((date?: string) => {
        if (!date) return '';
        if (!date.includes('/')) return date;

        const [month, day, year] = date.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }, []);

    const getFilteredRoles = useCallback(() => {
        const filterRoles = filterProp.dropdowns.globalRoles;
        return globalRoles.filter(role => filterRoles.keys.includes(role.name));
    }, [filterProp.dropdowns.globalRoles, globalRoles]);

    // Effects
    useEffect(() => {
        if (drawerProps.tenantId === oemTenantId) {
            setIsOEMTenant(true);
        }
    }, [drawerProps.tenantId]);

    useLayoutEffect(() => {
        if (currentId === DrawerIDs.User) {
            setDatesValidation({
                hasError: false,
                errorMessage: datesValidation.errorMessage
            });

            if (action === Operations.Link) {
                const today = dateToFrontEndFormat(toESTDateString(new Date()));

                setUserProfile(null);
                setNotes('');
                setStartDate(today);
                setEndDate('');
                setCurrentGlobalRoles([]);
                setDisableDependantFields(true);
                return;
            }

            dispatch(setSaveButtonEnabled(false));
        }
    }, [isOpenDrawer]);

    useLayoutEffect(() => {
        if (currentId === DrawerIDs.User && isEditFormInfoExists(action, editingItemInfo)) {
            const notesCurrentValue = editingItemInfo?.editingRow?.notes ?? '';
            const startDateCurrentValue = editingItemInfo?.editingRow?.startDate ?? '';
            const endDateCurrentValue = editingItemInfo?.editingRow?.endDate ?? '';

            setNotes(notesCurrentValue);
            setStartDate(startDateCurrentValue);
            setEndDate(endDateCurrentValue);
        }
    }, [
        isOpenDrawer,
        editingItemInfo?.editingRow?.notes,
        editingItemInfo?.editingRow?.startDate,
        editingItemInfo?.editingRow?.endDate
    ]);

    useEffect(() => {
        if (currentId === DrawerIDs.User) {
            if (
                action === Operations.Edit &&
                editingItemInfo?.editingRow?.rawGlobalRoles &&
                editingItemInfo?.editingRow?.rawGlobalRoles?.length > 0
            ) {
                const globalRolesIdsCurrentList = editingItemInfo?.editingRow?.rawGlobalRoles
                    ?.map(gr => gr?.id);
                setCurrentGlobalRoles(globalRolesIdsCurrentList);
            } else {
                setCurrentGlobalRoles([]);
            }
        }
    }, [
        isOpenDrawer,
        editingItemInfo?.editingRow?.rawGlobalRoles,
        currentId,
        action
    ]);

    useLayoutEffect(() => {
        if (currentId === DrawerIDs.User) {
            if (action === Operations.Link && userProfileInfoExists(userProfile)) {
                setDisableDependantFields(false);
            } else {
                setDisableDependantFields(true);
                dispatch(setSaveButtonEnabled(false));
            }
        }
    }, [
        isOpenDrawer,
        userProfile?.oemId,
        userProfile?.firstName,
        userProfile?.lastName,
        currentId,
        action,
        userProfileInfoExists,
        userProfile,
        dispatch
    ]);

    useEffect(() => {
        if (currentId === DrawerIDs.User) {
            if (action === Operations.Edit && editingItemInfo?.editingItemId) {
                dispatch(setSaveButtonEnabled(true));
            } else {
                dispatch(setSaveButtonEnabled(false));
            }
        }
    }, [isOpenDrawer, editingItemInfo?.editingItemId, currentId, action, dispatch]);

    useEffect(() => {
        const hasError = isStartDateBeforeEndDate(startDate, endDate) === false;
        setDatesValidation(prev => ({
            ...prev,
            hasError
        }));
    }, [startDate, endDate]);

    useEffect(() => {
        if (currentId === DrawerIDs.User) {
            const requiredFields = {
                startDate: startDate
            };
            const requiredFieldsAreEmpty = !Object
                .values(requiredFields)
                .every(fieldValue => fieldValue !== '');

            if (
                (requiredFieldsAreEmpty || datesValidation.hasError) ||
                (action === Operations.Link && !userProfileInfoExists(userProfile) ||
                    (action === Operations.Link && inactiveIdentity && !inactiveIdentityChecked))
            ) {
                dispatch(setSaveButtonEnabled(false));
            } else {
                dispatch(setSaveButtonEnabled(true));
            }
        }
    }, [
        isOpenDrawer,
        startDate,
        endDate,
        userProfile?.oemId,
        userProfile?.oemIdPName,
        editingItemInfo?.editingRow?.rawGlobalRoles,
        editingItemInfo?.editingItemId,
        currentGlobalRoles,
        inactiveIdentityChecked,
        currentId,
        datesValidation.hasError,
        action,
        userProfileInfoExists,
        userProfile,
        inactiveIdentity,
        dispatch
    ]);

    // Handlers
    const handleUserProfileChange = useCallback((_: unknown, value: UserProfileOemMapping | null) => {
        setUserProfile(value);
        if (value) {
            setInactiveIdentity(!value.activeIdentityStatus);
        } else {
            setInactiveIdentity(false);
            setInactiveIdentityChecked(false);
        }
        dispatch(setNameInConflict(false));
    }, [dispatch]);

    const handleGlobalRolesSelect = useCallback((value: string[]) => {
        setCurrentGlobalRoles(value);
    }, []);

    const getPostOptions = useCallback(() => {
        const body = {
            tenantId: drawerProps.tenantId,
            userId: userProfile?.oemId ?? '',
            notes: notes || '',
            globalRoles: currentGlobalRoles.map(x => ({
                id: x
            })),
            paginationInfo: {
                skip: currentPage * rowsPerPage,
                take: rowsPerPage,
                search: filterProp.keywords[0],
                globalRoles: getFilteredRoles(),
                status: filterProp.dropdowns.status.keys
            },
            startDate: formatDate(startDate)
        };

        if (endDate) {
            return { ...body, endDate: formatDate(endDate) };
        }

        return body;
    }, [drawerProps.tenantId, userProfile?.oemId, notes, currentGlobalRoles, currentPage, rowsPerPage, filterProp, getFilteredRoles, formatDate, startDate, endDate]);

    const getPutOptions = useCallback(() => {
        const body = {
            tenantId: drawerProps.tenantId,
            userId: editingItemInfo?.editingItemId ? String(editingItemInfo.editingItemId) : '',
            notes: notes || '',
            globalRoles: currentGlobalRoles.map(x => ({
                id: x
            })),
            paginationInfo: {
                skip: currentPage * rowsPerPage,
                take: rowsPerPage,
                search: filterProp.keywords[0],
                globalRoles: getFilteredRoles(),
                status: filterProp.dropdowns.status.keys
            },
            startDate: formatDate(startDate)
        };

        if (endDate) {
            return { ...body, endDate: formatDate(endDate) };
        }

        return body;
    }, [drawerProps.tenantId, editingItemInfo?.editingItemId, notes, currentGlobalRoles, currentPage, rowsPerPage, filterProp, getFilteredRoles, formatDate, startDate, endDate]);

    const createSuccessHandler = useCallback((postResult: { rows: GridValidRowModel[], count: number, skipped: number }) => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        dispatch(setPaginationGridData({
            rows: postResult.rows,
            rowsCount: postResult.count,
            currentPage: postResult.skipped / rowsPerPage,
            rowsPerPage: rowsPerPage,
            sortModel: sortModel
        }));
        const operationName = (action === Operations.Create) ? 'created' : 'linked';
        enqueueSnackbar(`The ${entityName} was successfully ${operationName}.`, { variant: 'success' });
    }, [dispatch, rowsPerPage, sortModel, action, entityName, enqueueSnackbar]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFailureHandler = useCallback((error: any) => {
        dispatch(setNameInConflict(false));
        switch (extractStatusCode(error.message)) {
            case 404: {
                if (currentPage === 0) {
                    enqueueSnackbar('The User was successfully linked.', { variant: 'success' });
                    dispatch(setPaginationGridData({}));
                    dispatch(closeDrawer());
                }
                dispatch(fetchTenantUserPaginatedMemberships({
                    tenantId: drawerProps.tenantId,
                    skip: 0,
                    take: rowsPerPage,
                    search: filterProp.keywords[0],
                    globalRoles: getFilteredRoles(),
                    status: filterProp.dropdowns.status.keys,
                    sortKey: sortModel?.[0]?.field,
                    sortOrder: sortModel?.[0]?.sort,
                }))
                    .unwrap()
                    .then((result) => {
                        dispatch(setPaginationGridData({
                            rows: result.data,
                            rowsCount: result.count,
                            currentPage: result.skipped / rowsPerPage,
                            rowsPerPage: rowsPerPage,
                            sortModel: sortModel
                        }));
                    })
                    .catch(() => {
                        dispatch(setPaginationGridData({}));
                    })
                    .finally(() => {
                        enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
                        dispatch(closeDrawer());
                    });
                break;
            }
            case 409: {
                dispatch(setNameInConflict(true));
                break;
            }
            default: {
                enqueueSnackbar(
                    'There was an error while linking the User.' +
                    DEFAULT_ERROR_MESSAGE,
                    { variant: 'error' }
                );
                break;
            }
        }
    }, [dispatch, currentPage, drawerProps.tenantId, rowsPerPage, filterProp, getFilteredRoles, sortModel, enqueueSnackbar]);

    const editSuccessHandler = useCallback((putResult: { rows: GridValidRowModel[], count: number, skipped: number }) => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        dispatch(setPaginationGridData({
            rows: putResult.rows,
            rowsCount: putResult.count,
            currentPage: putResult.skipped / rowsPerPage,
            rowsPerPage: rowsPerPage,
            sortModel: sortModel
        }));
        enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
    }, [dispatch, rowsPerPage, sortModel, enqueueSnackbar]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editFailureHandler = useCallback((error: any) => {
        dispatch(setNameInConflict(false));
        switch (extractStatusCode(error.message)) {
            case 404: {
                if (currentPage === 0) {
                    enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
                    dispatch(setPaginationGridData({}));
                    dispatch(closeDrawer());
                }
                dispatch(fetchTenantUserPaginatedMemberships({
                    tenantId: drawerProps.tenantId,
                    skip: 0,
                    take: rowsPerPage,
                    search: filterProp.keywords[0],
                    globalRoles: getFilteredRoles(),
                    status: filterProp.dropdowns.status.keys,
                    sortKey: sortModel?.[0]?.field,
                    sortOrder: sortModel?.[0]?.sort,
                }))
                    .unwrap()
                    .then((result) => {
                        dispatch(setPaginationGridData({
                            rows: result.data,
                            rowsCount: result.count,
                            currentPage: result.skipped / rowsPerPage,
                            rowsPerPage: rowsPerPage,
                            sortModel: sortModel
                        }));
                    })
                    .catch(() => {
                        dispatch(setPaginationGridData({}));
                    })
                    .finally(() => {
                        enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
                        dispatch(closeDrawer());
                    });
                break;
            }
            case 409: {
                dispatch(setNameInConflict(true));
                break;
            }
            default: {
                enqueueSnackbar(
                    'There was an error while editing the User.' +
                    DEFAULT_ERROR_MESSAGE,
                    { variant: 'error' }
                );
                break;
            }
        }
    }, [dispatch, currentPage, drawerProps.tenantId, rowsPerPage, filterProp, getFilteredRoles, sortModel, enqueueSnackbar]);

    const postCallback = useCallback(() => {
        const postOptions = getPostOptions();
        if (!postOptions) {
            return;
        }
        setProcessing(true);
        dispatch(postTenantUserMembership(postOptions))
            .unwrap()
            .then((postResult) => {
                createSuccessHandler(postResult);
            })
            .catch((error) => {
                createFailureHandler(error);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, [getPostOptions, dispatch, createSuccessHandler, createFailureHandler]);

    const putCallback = useCallback(() => {
        if (!editingItemInfo) {
            return;
        }
        const putOptions = getPutOptions();
        if (!putOptions) {
            return;
        }
        setProcessing(true);
        dispatch(putTenantUserMembership(putOptions))
            .unwrap()
            .then((putResult) => {
                editSuccessHandler(putResult);
            })
            .catch((error) => {
                editFailureHandler(error);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, [editingItemInfo, getPutOptions, dispatch, editSuccessHandler, editFailureHandler]);

    const tenantUserMembershipNotesProps = useMemo((): (shouldUseDisablePropEvaluation: boolean) => ExpandableTextAreaProps => {
        return (shouldUseDisablePropEvaluation: boolean) => ({
            textAreaCommonProps: {
                value: notes,
                onChange: (value: string) => setNotes(value),
                disabled: shouldUseDisablePropEvaluation ? disableDependantFields : false,
                placeholder: 'Type Notes',
                label: 'Notes'
            },
            popoutCommonProps: {
                popoutInputBaseProps: {
                    inputBasePlaceholder: 'Type Tenant Membership'
                },
                popoutInputLabel: 'Tenant Membership Notes (optional)'
            }
        });
    }, [notes, disableDependantFields]);

    return {
        // State
        processing,
        userProfile,
        currentGlobalRoles,
        notes,
        startDate,
        endDate,
        disableDependantFields,
        datesValidation,
        inactiveIdentity,
        inactiveIdentityChecked,
        globalRoles,
        nameInConflict,
        action,
        shouldDisableGlobalRoleField,
        // Setters
        setStartDate,
        setEndDate,
        setInactiveIdentityChecked,
        // Handlers
        handleUserProfileChange,
        handleGlobalRolesSelect,
        postCallback,
        putCallback,
        tenantUserMembershipNotesProps,
        // Props
        drawerProps,
        editingItemInfo,
        entityName
    };
}

function CreateContent({
    entityName,
    processing,
    userProfile,
    startDate,
    endDate,
    disableDependantFields,
    datesValidation,
    inactiveIdentity,
    inactiveIdentityChecked,
    globalRoles,
    nameInConflict,
    shouldDisableGlobalRoleField,
    setStartDate,
    setEndDate,
    setInactiveIdentityChecked,
    handleUserProfileChange,
    handleGlobalRolesSelect,
    postCallback,
    tenantUserMembershipNotesProps,
    drawerProps
}: Readonly<ReturnType<typeof useTenantUserMembershipDrawer>>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <UserProfilesDropDown
                    value={userProfile}
                    onChange={handleUserProfileChange}
                />
                {nameInConflict &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage='This User is already linked to this Tenant.'
                    />
                }
                <WarningMessageWithCheckboxAcknowledgement
                    show={inactiveIdentity}
                    checked={inactiveIdentityChecked}
                    warningMessage={INACTIVE_IDENTITY_WARNING_MESSAGE}
                    checkboxLabel={INACTIVE_IDENTITY_CHECKBOX_LABEL}
                    onCheckedChange={(checked) => setInactiveIdentityChecked(checked)}
                />
                <EntitiesMultipleSelect
                    title='Global Roles'
                    onChange={handleGlobalRolesSelect}
                    disabled={disableDependantFields || shouldDisableGlobalRoleField}
                    entities={globalRoles}
                    itemRenderer={renderGlobalRoleName}
                    useInfoTooltip
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
                        <PalmDatePicker
                            label='Start Date'
                            onChange={(value: string) => setStartDate(value)}
                            required={true}
                            value={startDate}
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={(value: string) => setEndDate(value)}
                            value={endDate}
                        ></PalmDatePicker>
                    </Stack>
                </LocalizationProvider>
                {datesValidation.hasError && (
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={datesValidation.errorMessage}
                    />
                )}
                <ExpandableTextArea
                    {...tenantUserMembershipNotesProps(true)}
                />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    currentGlobalRoles,
    startDate,
    endDate,
    datesValidation,
    globalRoles,
    nameInConflict,
    shouldDisableGlobalRoleField,
    setStartDate,
    setEndDate,
    handleGlobalRolesSelect,
    putCallback,
    tenantUserMembershipNotesProps,
    drawerProps,
    editingItemInfo
}: Readonly<ReturnType<typeof useTenantUserMembershipDrawer>>): JSX.Element {
    const userProfile = editingItemInfo?.editingRow?.profile;
    const userFullName = `${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`;

    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField label="Name" value={userFullName} />
                {nameInConflict &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage='This User is already linked to this Tenant.'
                    />
                }
                <EntitiesMultipleSelect
                    initialValue={currentGlobalRoles}
                    title='Global Roles'
                    onChange={handleGlobalRolesSelect}
                    entities={globalRoles}
                    itemRenderer={renderGlobalRoleName}
                    useInfoTooltip
                    disabled={shouldDisableGlobalRoleField}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
                        <PalmDatePicker
                            label='Start Date'
                            onChange={(value: string) => setStartDate(value)}
                            required={true}
                            value={startDate}
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={(value: string) => setEndDate(value)}
                            value={endDate}
                        ></PalmDatePicker>
                    </Stack>
                </LocalizationProvider>
                {datesValidation.hasError && (
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={datesValidation.errorMessage}
                    />
                )}
                <ExpandableTextArea
                    {...tenantUserMembershipNotesProps(false)}
                />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
            </Stack>
        </EditDrawer>
    );
}

export function TenantUserMembershipCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<UserTenantMembership & UserTenantItem>>) {
    const tenantDetails = useAppSelector(selectPageInfo);
    const tenantUserDrawerProps: UserTenantDrawerProperties = {
        tenantId: tenantDetails?.id ?? '',
        tenantName: tenantDetails?.name ?? ''
    };

    const drawerState = useTenantUserMembershipDrawer({
        entityName: title,
        drawerProps: tenantUserDrawerProps,
        editingItemInfo
    });

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return <CreateContent {...drawerState} />;
    }

    if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return <EditContent {...drawerState} />;
    }

    return <></>;
}
